/**
 * SA-759 (spec §B7): load a mailbox's earlier mail. A date range (default
 * the last 14 days), what runs on it (`capture` preselected; `propose` only
 * on a router; `live` with a line naming what it runs), Count, then Start.
 * SA-986: a Classify ahead checkbox on an email router run that classifies.
 * The words live in `backloadText.ts`.
 */
import { useMemo, useState } from 'react'
import {
  Alert, AlertDescription, Button, Input, Modal, ModalBody, ModalContent, ModalDescription,
  ModalFooter, ModalHeader, ModalTitle,
} from '@/components/ui'
import { usePreviewBackload, useStartBackload } from '@/hooks/useMailboxes'
import type { BackloadMode, BackloadPreview, Mailbox } from '@/services/mailboxes'
import {
  backloadErrorText, backloadWords, CLASSIFY_AHEAD_WORDS, classifyAheadOffered, defaultDates, modeOptions, previewLine, rangeFromDates,
} from './backloadText'

interface BackloadDialogProps {
  mailbox: Mailbox
  open: boolean
  onOpenChange: (open: boolean) => void
}

export function BackloadDialog({ mailbox, open, onOpenChange }: BackloadDialogProps) {
  const initial = useMemo(() => defaultDates(new Date()), [])
  const [startDate, setStartDate] = useState(initial.start)
  const [endDate, setEndDate] = useState(initial.end)
  const [mode, setMode] = useState<BackloadMode>('capture')
  const [classifyAhead, setClassifyAhead] = useState(false)
  // The count is tied to the range it counted: a result that lands after a date
  // change carries the old key and is ignored.
  const [counted, setCounted] = useState<{ key: string; result: BackloadPreview } | null>(null)
  const [error, setError] = useState<string | null>(null)
  const previewMutation = usePreviewBackload()
  const startMutation = useStartBackload()
  const rangeKey = `${startDate}|${endDate}`
  const preview = counted && counted.key === rangeKey ? counted.result : null
  // SA-947 (spec P10): a WhatsApp group source speaks of messages.
  const words = backloadWords(mailbox.channel)
  const options = modeOptions(mailbox.sink_type, words)
  const aheadOffered = classifyAheadOffered(mailbox, mode)
  const selected = options.find((option) => option.mode === mode) ?? options[0]

  const changeDate = (setter: (value: string) => void) => (value: string) => {
    setter(value)
    setCounted(null)
    setError(null)
  }

  const handleCount = async () => {
    setError(null)
    const key = rangeKey
    try {
      const result = await previewMutation.mutateAsync({ id: mailbox.id, ...rangeFromDates(startDate, endDate) })
      setCounted({ key, result })
    } catch (err) {
      setCounted(null)
      setError(backloadErrorText(err))
    }
  }

  const handleStart = async () => {
    setError(null)
    try {
      await startMutation.mutateAsync({ id: mailbox.id, ...rangeFromDates(startDate, endDate), mode,
        ...(aheadOffered ? { classify_ahead: classifyAhead } : {}),
      })
      onOpenChange(false)
    } catch (err) {
      setError(backloadErrorText(err))
    }
  }

  return (
    <Modal open={open} onOpenChange={onOpenChange}>
      <ModalContent size="md">
        <ModalHeader>
          <ModalTitle>{words.title}</ModalTitle>
          <ModalDescription>
            {words.description(mailbox.display_name)}
          </ModalDescription>
        </ModalHeader>
        <ModalBody className="space-y-4">
          <div className="grid grid-cols-2 gap-3">
            <Input
              id={`backload-start-${mailbox.id}`}
              label="From"
              type="date"
              value={startDate}
              onChange={(e) => changeDate(setStartDate)(e.target.value)}
            />
            <Input
              id={`backload-end-${mailbox.id}`}
              label="Through"
              type="date"
              value={endDate}
              onChange={(e) => changeDate(setEndDate)(e.target.value)}
            />
          </div>
          <fieldset className="space-y-2">
            <legend className="text-sm font-medium text-foreground">{words.modeLegend}</legend>
            {options.map((option) => (
              <label key={option.mode} className="flex items-start gap-2 text-sm">
                <input
                  type="radio"
                  name={`backload-mode-${mailbox.id}`}
                  value={option.mode}
                  checked={mode === option.mode}
                  onChange={() => setMode(option.mode)}
                  className="mt-1"
                />
                <span>
                  <span className="font-medium text-foreground">{option.label}</span>
                  <span className="block text-xs text-muted-foreground">{option.description}</span>
                </span>
              </label>
            ))}
          </fieldset>
          {aheadOffered && (
            <label className="flex items-start gap-2 text-sm">
              <input
                type="checkbox"
                checked={classifyAhead}
                onChange={(e) => setClassifyAhead(e.target.checked)}
                className="mt-1"
              />
              <span>
                <span className="font-medium text-foreground">{CLASSIFY_AHEAD_WORDS.label}</span>
                <span className="block text-xs text-muted-foreground">{CLASSIFY_AHEAD_WORDS.description}</span>
              </span>
            </label>
          )}
          {selected.warning && (
            <Alert variant="warning">
              <AlertDescription>{selected.warning}</AlertDescription>
            </Alert>
          )}
          {preview && <p className="text-sm text-foreground">{previewLine(preview, words)}</p>}
          {error && (
            <Alert variant="destructive">
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}
          <p className="text-xs text-muted-foreground">
            {words.footnote}
          </p>
        </ModalBody>
        <ModalFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)}>
            Close
          </Button>
          <Button
            variant="outline"
            onClick={handleCount}
            disabled={!startDate || !endDate || previewMutation.isPending}
          >
            Count
          </Button>
          <Button
            onClick={handleStart}
            disabled={!preview || preview.to_load === 0 || startMutation.isPending}
          >
            Start
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  )
}
